import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import type { Review } from '@vibesites/schema';
import {
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
  useSlotGroupOverrides,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

// ─── Star icon ────────────────────────────────────────────────────────────────

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.5}
      viewBox="0 0 20 20"
      className={`w-4 h-4 flex-shrink-0 ${filled ? 'text-[var(--vibe-color-secondary)]' : 'text-[var(--vibe-color-border)]'}`}
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

// ─── Slot group definitions ───────────────────────────────────────────────────

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Reviews Page Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/reviews' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Read All Reviews' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function ReviewsCallout() {
  const eyebrow = useContentToken('reviewscallout.eyebrow');
  const ctaLink = useSlotGroup('reviewscallout.cta_link', ctaLinkDef);
  const slotGroupOverrides = useSlotGroupOverrides();

  const reviews = useCollectionBinding<Review>('reviewscallout.items', {
    collection: 'reviews',
    defaultLimit: 3,
  });

  const displayReviews = reviews.slice(0, 3);

  const renderStars = (rating: number = 5) => (
    <div className="flex items-center gap-0.5 mb-5">
      {Array.from({ length: 5 }, (_, i) => (
        <StarIcon key={i} filled={i < rating} />
      ))}
    </div>
  );

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Section header ─────────────────────────────────── */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <EditableToken
              path="reviewscallout.eyebrow"
              defaultValue="Resident reviews"
              as="p"
              className="font-body text-sm text-[var(--vibe-color-text)] mb-3 tracking-wide"
            >
              {eyebrow}
            </EditableToken>

            <RichTextContent
              path="reviewscallout.title"
              label="Title"
              defaultValue='<p>What Residents Say About <span data-type="field-token" data-field="property_name"></span></p>'
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text)] leading-[1.1]"
            />
          </div>

          <EditableSlotGroup
            groupPath="reviewscallout.cta_link"
            definition={ctaLinkDef}
            override={slotGroupOverrides['reviewscallout.cta_link']}
          >
            <a
              href={ctaLink.url || '/reviews'}
              target={ctaLink._target || '_self'}
              className="inline-flex items-center flex-shrink-0 px-6 py-3 text-sm font-semibold text-[var(--vibe-color-text)] border border-[var(--vibe-color-border)] rounded-sm hover:bg-[var(--vibe-color-text)] hover:text-white transition-all duration-200"
            >
              {ctaLink.label || 'Read All Reviews'}
            </a>
          </EditableSlotGroup>
        </div>

        {/* ── Review cards ───────────────────────────────────── */}
        {displayReviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {displayReviews.map((review: Review, i: number) => {
              const numericRating =
                review.rating != null ? parseInt(String(review.rating), 10) || 5 : 5;
              return (
                <div
                  key={i}
                  className="bg-white rounded-xl p-8 shadow-sm flex flex-col"
                >
                  {renderStars(numericRating)}

                  {hasValue(review.text) && (
                    <p className="font-body text-sm text-[var(--vibe-color-text)] leading-relaxed flex-grow line-clamp-5 mb-6">
                      &ldquo;{review.text}&rdquo;
                    </p>
                  )}

                  <div className="mt-auto pt-4 border-t border-[var(--vibe-color-border)]">
                    {hasValue(review.name) && (
                      <p className="font-body text-sm font-semibold text-[var(--vibe-color-text)]">
                        {review.name}
                      </p>
                    )}
                    {hasValue(review.date) && (
                      <p className="text-xs text-[var(--vibe-color-text-muted)] mt-1">
                        {review.date}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Resident reviews will be displayed here.</p>
          </div>
        )}

      </div>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsCallout',
  displayName: 'Reviews Callout',
  tokens: {
    'reviewscallout.eyebrow': {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Resident reviews',
    },
    'reviewscallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>What Residents Say About <span data-type="field-token" data-field="property_name"></span></p>',
    },
  },
  slotGroups: {
    'reviewscallout.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'reviewscallout.items': {
      label: 'Reviews',
      description: 'Featured resident reviews',
      collection: 'reviews',
      defaultLimit: 3,
    },
  },
};

(ReviewsCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ReviewsCallout;
